import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { apiRequest, API_BASE_URL } from '../api';

import '../css/CRMSourceIntegrationManagement.css';

const CRMSourceIntegrationManagement = () => {
    const [integrations, setIntegrations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showTestModal, setShowTestModal] = useState(false);
    const [showFieldMappingsModal, setShowFieldMappingsModal] = useState(false);
    const [showCredentialsModal, setShowCredentialsModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedIntegration, setSelectedIntegration] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [testResults, setTestResults] = useState(null);
    const [instructions, setInstructions] = useState(null);
    const [fieldMappings, setFieldMappings] = useState([]);
    const [errors, setErrors] = useState({});
    const [activeTab, setActiveTab] = useState('integrations');
    const [feedbackMessage, setFeedbackMessage] = useState('');
    const [feedbackType, setFeedbackType] = useState('success'); // 'success' or 'error'
    
    // Form data for create/edit
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        integration_type: 'event_sync',
        config: {},
        field_mappings: []
    });

    // Available fields for mapping
    const availableFields = {
        tour: [
            { value: 'id', label: 'Tour ID', displayName: 'Tour ID', description: 'Unique identifier for the tour' },
            { value: 'name', label: 'Tour Name', displayName: 'Tour Name', description: 'Name of the tour' },
            { value: 'country', label: 'Country', displayName: 'Country', description: 'Country where the tour takes place' },
            { value: 'date_from', label: 'Start Date', displayName: 'Tour Start Date', description: 'Tour start date (YYYY-MM-DD)' },
            { value: 'date_to', label: 'End Date', displayName: 'Tour End Date', description: 'Tour end date (YYYY-MM-DD)' },
            { value: 'crm_id', label: 'CRM ID', displayName: 'External ID', description: 'Unique identifier for the tour from your CRM system' },
            { value: 'assigned_user_id', label: 'Assigned User ID', displayName: 'Assigned User', description: 'ID of the user assigned to the tour' }
        ],
        event: [
            { value: 'name', label: 'Event Name', displayName: 'Event Name', description: 'Name of the event' },
            { value: 'event_date', label: 'Event Date', displayName: 'Event Date', description: 'Date when the event occurs (YYYY-MM-DD)' },
            { value: 'event_type', label: 'Event Type', displayName: 'Event Type', description: 'Type of event (e.g., Open House, Campus Tour)' },
            { value: 'form_type', label: 'Form Type', displayName: 'Form Type', description: 'Type of form to use for this event (e.g., Undergraduate, Graduate, Hybrid)' },
            { value: 'city', label: 'City', displayName: 'City', description: 'City where the event takes place' },
            { value: 'country', label: 'Country', displayName: 'Country', description: 'Country where the event takes place' },
            { value: 'crm_id', label: 'CRM ID', displayName: 'External ID', description: 'Unique identifier for the event from your CRM system' },
            { value: 'assigned_user_id', label: 'Assigned User ID', displayName: 'Assigned User', description: 'ID of the user assigned to the event' }
        ]
    };
    
    const navigate = useNavigate();
    const userRole = localStorage.getItem('role');
    const universityId = localStorage.getItem('universityId');

    useEffect(() => {
        fetchIntegrations();
    }, []);

    const fetchIntegrations = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await apiRequest(`${API_BASE_URL}/crm-source-integrations`, 'GET', null, token);

            // Backend returns direct array, not wrapped in success/data
            if (Array.isArray(response)) {
                setIntegrations(response);
            } else {
                console.error('Unexpected response format:', response);
                setIntegrations([]);
            }
        } catch (error) {
            console.error('Error fetching integrations:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchFieldMappings = async (integrationId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await apiRequest(`${API_BASE_URL}/crm-source-integrations/${integrationId}/field-mappings`, 'GET', null, token);
            
            // Backend returns direct array
            if (Array.isArray(response)) {
                setFieldMappings(response);
            } else {
                setFieldMappings([]);
            }
        } catch (error) {
            console.error('Error fetching field mappings:', error);
        }
    };

    const showFeedback = (message, type = 'success') => {
        setFeedbackMessage(message);
        setFeedbackType(type);
        
        // Auto-hide after 5 seconds
        setTimeout(() => {
            setFeedbackMessage('');
        }, 5000);
    };

    const fetchInstructions = async (integrationId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await apiRequest(`${API_BASE_URL}/crm-source-integrations/${integrationId}/instructions`, 'GET', null, token);
            
            // Backend returns direct response
            if (response) {
                setInstructions(response);
            }
        } catch (error) {
            console.error('Error fetching instructions:', error);
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            description: '',
            integration_type: 'event_sync',
            config: {},
            field_mappings: []
        });
        setErrors({});
    };

    const handleCreateIntegration = async (modalFormData) => {
        try {
            setIsSubmitting(true);
            setErrors({});
            
            const token = localStorage.getItem('token');
            const response = await apiRequest(`${API_BASE_URL}/crm-source-integrations`, 'POST', modalFormData, token);

            // Backend returns direct response, not wrapped in success/data
            if (response && response.id) {
                setShowCreateModal(false);
                resetForm();
                fetchIntegrations();
                showFeedback('Integration created successfully! You can now view your API credentials by clicking "View Credentials" on the integration card.');
            } else {
                setErrors({ general: 'Failed to create integration' });
            }
        } catch (error) {
            console.error('Error creating integration:', error);
            setErrors({ general: 'Failed to create integration' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleUpdateIntegration = async (modalFormData) => {
        try {
            setIsSubmitting(true);
            setErrors({});
            
            const token = localStorage.getItem('token');
            const response = await apiRequest(`${API_BASE_URL}/crm-source-integrations/${selectedIntegration.id}`, 'PUT', modalFormData, token);

            // Backend returns direct response, not wrapped in success/data
            if (response && response.id) {
                setShowEditModal(false);
                resetForm();
                fetchIntegrations();
                showFeedback('Integration updated successfully!');
            } else {
                setErrors({ general: 'Failed to update integration' });
            }
        } catch (error) {
            console.error('Error updating integration:', error);
            setErrors({ general: 'Failed to update integration' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteIntegration = async (integrationId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await apiRequest(`${API_BASE_URL}/crm-source-integrations/${integrationId}`, 'DELETE', null, token);

            // Backend returns direct response
            if (response && response.message) {
                showFeedback('Integration deleted successfully!');
                fetchIntegrations();
            }
        } catch (error) {
            console.error('Error deleting integration:', error);
        }
    };

    const openDeleteModal = (integration) => {
        setSelectedIntegration(integration);
        setShowDeleteModal(true);
    };

    const handleRegenerateApiKey = async (integrationId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await apiRequest(`${API_BASE_URL}/crm-source-integrations/${integrationId}/regenerate-api-key`, 'POST', null, token);

            // Backend returns direct response
            if (response && response.new_api_key) {
                showFeedback(`New API Key generated: ${response.new_api_key}`);
                fetchIntegrations();
            }
        } catch (error) {
            console.error('Error regenerating API key:', error);
        }
    };

    const handleTestIntegration = async (integrationId, testData) => {
        try {
            const token = localStorage.getItem('token');
            const response = await apiRequest(`${API_BASE_URL}/crm-source-integrations/${integrationId}/test`, 'POST', { test_data: testData }, token);

            // Backend returns direct response
            if (response) {
                setTestResults(response);
            }
        } catch (error) {
            console.error('Error testing integration:', error);
        }
    };

    const openCreateModal = () => {
        resetForm();
        setShowCreateModal(true);
    };

    const openCredentialsModal = (integration) => {
        console.log('Opening credentials modal for integration:', integration);
        setSelectedIntegration(integration);
        setShowCredentialsModal(true);
    };

    const openEditModal = (integration) => {
        setSelectedIntegration(integration);
        setFormData({
            name: integration.name,
            description: integration.description,
            integration_type: integration.integration_type,
            config: integration.config || {},
            field_mappings: integration.field_mappings || []
        });
        setShowEditModal(true);
    };



    const openTestModal = (integration) => {
        setSelectedIntegration(integration);
        setTestResults(null);
        setShowTestModal(true);
    };

    const openFieldMappingsModal = async (integration) => {
        setSelectedIntegration(integration);
        await fetchFieldMappings(integration.id);
        setShowFieldMappingsModal(true);
    };

    const getStatusBadge = (integration) => {
        return integration.is_active ? 
            <span className="badge badge-success">Active</span> : 
            <span className="badge badge-danger">Inactive</span>;
    };

    if (loading) {
        return (
            <div className="dashboard">
                <Sidebar />
                <div className="main-content">
                    <div className="content">
                        <div className="loading">Loading...</div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard">
            <Sidebar />
            <div className="main-content">
                <div className="content">
                    <div className="content-header">
                        <h1>CRM Source Integrations</h1>
                        <p>Manage integrations that allow external CRMs to push events into your system</p>
                    </div>

                    {/* Feedback Message */}
                    {feedbackMessage && (
                        <div 
                            className={`feedback-message ${feedbackType}`}
                            style={{
                                padding: '12px 16px',
                                marginBottom: '20px',
                                borderRadius: '6px',
                                border: '1px solid',
                                animation: 'fadeIn 0.3s ease-in',
                                backgroundColor: feedbackType === 'success' ? '#d4edda' : '#f8d7da',
                                borderColor: feedbackType === 'success' ? '#c3e6cb' : '#f5c6cb',
                                color: feedbackType === 'success' ? '#155724' : '#721c24'
                            }}
                        >
                            <i className={`fas ${feedbackType === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`}></i>
                            {feedbackMessage}
                        </div>
                    )}

                    <div className="tabs-container">
                        <div className="tabs-header">
                            <button 
                                className={`tab-button ${activeTab === 'integrations' ? 'active' : ''}`}
                                onClick={() => setActiveTab('integrations')}
                            >
                                <i className="fas fa-plug"></i> Integrations
                            </button>
                            <button 
                                className={`tab-button ${activeTab === 'guide' ? 'active' : ''}`}
                                onClick={() => setActiveTab('guide')}
                            >
                                <i className="fas fa-book"></i> Setup Guide
                            </button>
                        </div>

                        <div className="tab-content">
                            {activeTab === 'integrations' && (
                                <div className="integrations-tab">
                                    <div className="tab-header-actions">
                                        <button 
                                            className="btn btn-primary" 
                                            onClick={openCreateModal}
                                        >
                                            <i className="fas fa-plus"></i> Create Integration
                                        </button>
                                    </div>

                                    <div className="integrations-grid">
                                        {integrations.length === 0 ? (
                                            <div className="empty-state">
                                                <i className="fas fa-plug"></i>
                                                <h3>No CRM Source Integrations</h3>
                                                <p>Create your first integration to start receiving events from external CRMs.</p>
                                                <button className="btn btn-primary" onClick={openCreateModal}>
                                                    Create Integration
                                                </button>
                                            </div>
                                        ) : (
                                            integrations.map((integration) => (
                                                <div key={integration.id} className="integration-card">
                                                    <div className="integration-header">
                                                        <h3>{integration.name}</h3>
                                                        {getStatusBadge(integration)}
                                                    </div>
                                                    
                                                    {integration.description && (
                                                        <p className="integration-description">{integration.description}</p>
                                                    )}
                                                    
                                                    <div className="integration-details">
                                                        <div className="detail-item">
                                                            <span className="label">Type:</span>
                                                            <span className="value">{integration.integration_type}</span>
                                                        </div>
                                                        <div className="detail-item">
                                                            <span className="label">API Key:</span>
                                                            <span className="value api-key">
                                                                {integration.api_key ? 
                                                                    `${integration.api_key.substring(0, 8)}...` : 
                                                                    'Not generated'
                                                                }
                                                            </span>
                                                        </div>
                                                        <div className="detail-item">
                                                            <span className="label">Field Mappings:</span>
                                                            <span className="value">{integration.field_mappings?.length || 0}</span>
                                                        </div>
                                                    </div>

                                                    <div className="integration-actions">
                                                        <button 
                                                            className="btn btn-sm btn-outline-info"
                                                            onClick={() => openCredentialsModal(integration)}
                                                        >
                                                            <i className="fas fa-eye"></i> View Credentials
                                                        </button>
                                                        <button 
                                                            className="btn btn-sm btn-outline-primary"
                                                            onClick={() => openEditModal(integration)}
                                                        >
                                                            <i className="fas fa-edit"></i> Edit
                                                        </button>
                                                        <button 
                                                            className="btn btn-sm btn-outline-secondary"
                                                            onClick={() => openFieldMappingsModal(integration)}
                                                        >
                                                            <i className="fas fa-map"></i> Field Mappings
                                                        </button>
                                                        <button 
                                                            className="btn btn-sm btn-outline-warning"
                                                            onClick={() => openTestModal(integration)}
                                                        >
                                                            <i className="fas fa-vial"></i> Test
                                                        </button>
                                                        <button 
                                                            className="btn btn-sm btn-outline-success"
                                                            onClick={() => handleRegenerateApiKey(integration.id)}
                                                        >
                                                            <i className="fas fa-key"></i> Regenerate API Key
                                                        </button>
                                                        <button 
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => openDeleteModal(integration)}
                                                        >
                                                            <i className="fas fa-trash"></i> Delete
                                                        </button>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            )}

                            {activeTab === 'guide' && (
                                <div className="guide-tab">
                                    <GeneralInstructions setActiveTab={setActiveTab} />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Create Integration Modal */}
            {showCreateModal && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 9999,
                    }}
                >
                    <IntegrationModal
                        title="Create CRM Source Integration"
                        formData={formData}
                        onSubmit={handleCreateIntegration}
                        onClose={() => setShowCreateModal(false)}
                        isSubmitting={isSubmitting}
                        errors={errors}
                        availableFields={availableFields}
                    />
                </div>
            )}

            {/* Edit Integration Modal */}
            {showEditModal && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 9999,
                    }}
                >
                    <IntegrationModal
                        title="Edit CRM Source Integration"
                        formData={formData}
                        onSubmit={handleUpdateIntegration}
                        onClose={() => setShowEditModal(false)}
                        isSubmitting={isSubmitting}
                        errors={errors}
                        availableFields={availableFields}
                    />
                </div>
            )}



            {/* Test Integration Modal */}
            {showTestModal && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 9999,
                    }}
                >
                    <TestIntegrationModal
                        integration={selectedIntegration}
                        testResults={testResults}
                        onTest={handleTestIntegration}
                        onClose={() => setShowTestModal(false)}
                        showFeedback={showFeedback}
                    />
                </div>
            )}

            {/* Field Mappings Modal */}
            {showFieldMappingsModal && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 9999,
                    }}
                >
                    <FieldMappingsModal
                        integration={selectedIntegration}
                        fieldMappings={fieldMappings}
                        onClose={() => setShowFieldMappingsModal(false)}
                    />
                </div>
            )}

            {/* Credentials Modal */}
            {showCredentialsModal && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 9999,
                    }}
                >
                    <CredentialsModal
                        integration={selectedIntegration}
                        onClose={() => setShowCredentialsModal(false)}
                        showFeedback={showFeedback}
                    />
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 9999,
                    }}
                >
                    <DeleteConfirmationModal
                        integration={selectedIntegration}
                        onConfirm={handleDeleteIntegration}
                        onClose={() => setShowDeleteModal(false)}
                    />
                </div>
            )}
        </div>
    );
};

// Delete Confirmation Modal Component
const DeleteConfirmationModal = ({ integration, onConfirm, onClose }) => {
    const [deleteText, setDeleteText] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);

    const handleConfirm = async () => {
        if (deleteText !== 'DELETE') {
            return;
        }

        setIsDeleting(true);
        try {
            await onConfirm(integration.id);
            onClose();
        } catch (error) {
            console.error('Error deleting integration:', error);
        } finally {
            setIsDeleting(false);
        }
    };

    const isDeleteButtonEnabled = deleteText === 'DELETE' && !isDeleting;

    return (
        <div
            style={{
                backgroundColor: "#fff",
                padding: "20px",
                borderRadius: "8px",
                width: "500px",
                maxHeight: "90vh",
                overflowY: "auto",
                position: "relative",
            }}
        >
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "20px",
                    paddingBottom: "10px",
                    borderBottom: "1px solid #e0e0e0",
                }}
            >
                <h2 style={{ margin: 0, color: '#dc3545' }}>Delete Integration</h2>
                <button
                    onClick={onClose}
                    style={{
                        background: "none",
                        border: "none",
                        fontSize: "24px",
                        cursor: "pointer",
                        color: "#666",
                    }}
                >
                    ×
                </button>
            </div>
            
            <div className="modal-body">
                <div style={{ marginBottom: '20px' }}>
                    <p style={{ marginBottom: '15px', color: '#6c757d' }}>
                        You are about to delete the integration <strong>"{integration?.name}"</strong>. 
                        This action cannot be undone.
                    </p>
                    
                    <div style={{ 
                        padding: '15px', 
                        backgroundColor: '#fff3cd', 
                        border: '1px solid #ffeaa7',
                        borderRadius: '6px',
                        marginBottom: '20px'
                    }}>
                        <p style={{ margin: 0, color: '#856404' }}>
                            <i className="fas fa-exclamation-triangle" style={{ marginRight: '8px' }}></i>
                            <strong>Warning:</strong> Deleting this integration will:
                        </p>
                        <ul style={{ margin: '10px 0 0 20px', color: '#856404' }}>
                            <li>Remove all field mappings</li>
                            <li>Invalidate the API key</li>
                            <li>Stop all incoming event syncs</li>
                        </ul>
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                            To confirm deletion, type <strong>DELETE</strong> in the field below:
                        </label>
                        <input
                            type="text"
                            value={deleteText}
                            onChange={(e) => setDeleteText(e.target.value)}
                            placeholder="Type DELETE to confirm"
                            style={{
                                width: '100%',
                                padding: '10px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '14px'
                            }}
                        />
                    </div>
                </div>
            </div>
            
            <div
                style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "10px",
                    padding: "20px",
                    borderTop: "1px solid #e0e0e0",
                }}
            >
                <button 
                    className="btn btn-secondary" 
                    onClick={onClose}
                    disabled={isDeleting}
                >
                    Cancel
                </button>
                <button 
                    className="btn btn-danger" 
                    onClick={handleConfirm}
                    disabled={!isDeleteButtonEnabled}
                    style={{
                        opacity: isDeleteButtonEnabled ? 1 : 0.6,
                        cursor: isDeleteButtonEnabled ? 'pointer' : 'not-allowed'
                    }}
                >
                    {isDeleting ? (
                        <>
                            <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
                            Deleting...
                        </>
                    ) : (
                        <>
                            <i className="fas fa-trash" style={{ marginRight: '8px' }}></i>
                            Delete Integration
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

// Integration Modal Component


const IntegrationModal = ({ title, formData, onSubmit, onClose, isSubmitting, errors, availableFields }) => {
    const [localFormData, setLocalFormData] = useState(formData);
    const [newFieldMapping, setNewFieldMapping] = useState({
        external_field_name: '',
        internal_field_name: ''
    });

    useEffect(() => {
        setLocalFormData({
            ...formData,
            field_mappings: formData.field_mappings || []
        });
    }, [formData]);

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit(localFormData);
    };

    const addFieldMapping = () => {
        if (newFieldMapping.external_field_name && newFieldMapping.internal_field_name) {
            setLocalFormData(prev => ({
                ...prev,
                field_mappings: [...prev.field_mappings, { ...newFieldMapping }]
            }));
            setNewFieldMapping({
                external_field_name: '',
                internal_field_name: ''
            });
        }
    };

    const removeFieldMapping = (index) => {
        setLocalFormData(prev => ({
            ...prev,
            field_mappings: prev.field_mappings.filter((_, i) => i !== index)
        }));
    };

    return (
        <div
            style={{
                backgroundColor: "#fff",
                padding: "20px",
                borderRadius: "8px",
                width: "900px",
                maxHeight: "90vh",
                overflowY: "auto",
                position: "relative",
            }}
        >
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "20px",
                    paddingBottom: "10px",
                    borderBottom: "1px solid #e0e0e0",
                }}
            >
                <h2 style={{ margin: 0 }}>{title}</h2>
                <button
                    onClick={onClose}
                    style={{
                        background: "none",
                        border: "none",
                        fontSize: "24px",
                        cursor: "pointer",
                        color: "#666",
                    }}
                >
                    ×
                </button>
            </div>
            
            <form onSubmit={handleSubmit}>
                <div className="modal-body">
                    <div className="form-group">
                        <label>Integration Name *</label>
                        <input
                            type="text"
                            value={localFormData.name}
                            onChange={(e) => setLocalFormData(prev => ({ ...prev, name: e.target.value }))}
                            className={errors.name ? 'error' : ''}
                            required
                        />
                        {errors.name && <span className="error-message">{errors.name}</span>}
                    </div>

                    <div className="form-group">
                        <label>Description</label>
                        <textarea
                            value={localFormData.description}
                            onChange={(e) => setLocalFormData(prev => ({ ...prev, description: e.target.value }))}
                            rows="3"
                        />
                    </div>

                    <div className="form-group">
                        <label>Field Mappings</label>
                        <div className="field-mappings-section">
                            <p style={{ color: '#666', marginBottom: '15px' }}>
                                Map your CRM fields to Kai system fields. This tells the system how to convert your data.
                            </p>
                            
                            {localFormData.field_mappings && localFormData.field_mappings.length > 0 && (
                                <div className="existing-mappings">
                                    <h4>Current Mappings</h4>
                                    {localFormData.field_mappings.map((mapping, index) => (
                                        <div key={index} style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: '10px',
                                            margin: '5px 0',
                                            backgroundColor: '#f8f9fa',
                                            border: '1px solid #e0e0e0',
                                            borderRadius: '4px'
                                        }}>
                                                                                            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    <span style={{ fontWeight: 'bold', color: '#007bff' }}>
                                                        {mapping.external_field_name}
                                                    </span>
                                                    <span style={{ color: '#666' }}>→</span>
                                                    <span style={{ fontWeight: 'bold', color: '#28a745' }}>
                                                        {mapping.internal_field_name}
                                                    </span>
                                                </div>
                                            <button
                                                type="button"
                                                onClick={() => removeFieldMapping(index)}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    color: '#dc3545',
                                                    cursor: 'pointer',
                                                    padding: '5px',
                                                }}
                                            >
                                                <i className="fas fa-trash"></i>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="add-field-mapping">
                                <h4>Add New Field Mapping</h4>
                                
                                <div style={{ marginBottom: '15px' }}>
                                    <label>Select Kai Field:</label>
                                    <select
                                        value={newFieldMapping.internal_field_name}
                                        onChange={(e) => {
                                            const selectedField = e.target.value;
                                            setNewFieldMapping(prev => ({
                                                ...prev,
                                                internal_field_name: selectedField
                                            }));
                                        }}
                                        style={{ width: '100%', padding: '8px', marginTop: '5px' }}
                                    >
                                        <option value="">-- Select a Kai field --</option>
                                        <optgroup label="Tour Fields">
                                            {availableFields.tour.map((field) => (
                                                <option key={field.value} value={field.value}>
                                                    {field.displayName} - {field.description}
                                                </option>
                                            ))}
                                        </optgroup>
                                        <optgroup label="Event Fields">
                                            {availableFields.event.map((field) => (
                                                <option key={field.value} value={field.value}>
                                                    {field.displayName} - {field.description}
                                                </option>
                                            ))}
                                        </optgroup>
                                    </select>
                                </div>

                                <div style={{ marginBottom: '15px' }}>
                                    <label>Your CRM Field Name:</label>
                                    <input
                                        type="text"
                                        placeholder="e.g., event_title, tour_name, event_date"
                                        value={newFieldMapping.external_field_name}
                                        onChange={(e) => setNewFieldMapping(prev => ({ ...prev, external_field_name: e.target.value }))}
                                        style={{ width: '100%', padding: '8px', marginTop: '5px' }}
                                    />
                                </div>



                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={addFieldMapping}
                                    disabled={!newFieldMapping.external_field_name || !newFieldMapping.internal_field_name}
                                >
                                    <i className="fas fa-plus"></i> Add Mapping
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={onClose}>
                        Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                        {isSubmitting ? 'Saving...' : 'Save Integration'}
                    </button>
                </div>
            </form>
        </div>
    );
};

// Instructions Modal Component
const InstructionsModal = ({ integration, instructions, onClose }) => {
    return (
        <div className="modal-overlay">
            <div className="modal modal-large">
                <div className="modal-header">
                    <h2>Setup Instructions - {integration.name}</h2>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>
                <div className="modal-body">
                    <div className="instructions-section">
                        <h3>API Configuration</h3>
                        <div className="instruction-item">
                            <strong>API Endpoint:</strong>
                            <code>{instructions.api_endpoint}</code>
                        </div>
                        <div className="instruction-item">
                            <strong>API Key:</strong>
                            <code>{instructions.api_key}</code>
                        </div>
                    </div>

                    <div className="instructions-section">
                        <h3>Field Mapping Guide</h3>
                        <div className="field-mapping-guide">
                            {Object.entries(instructions.field_mapping_guide).map(([external, internal]) => (
                                <div key={external} className="mapping-item">
                                    <span className="external-field">{external}</span>
                                    <span className="arrow">→</span>
                                    <span className="internal-field">{internal}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="instructions-section">
                        <h3>Sample Payload</h3>
                        <pre className="sample-payload">
                            {JSON.stringify(instructions.sample_payload, null, 2)}
                        </pre>
                    </div>

                    <div className="instructions-section">
                        <h3>Setup Steps</h3>
                        <ol className="setup-steps">
                            {instructions.setup_steps.map((step, index) => (
                                <li key={index}>{step}</li>
                            ))}
                        </ol>
                    </div>
                </div>
                <div className="modal-footer">
                    <button className="btn btn-primary" onClick={onClose}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

// Test Integration Modal Component
const TestIntegrationModal = ({ integration, testResults, onTest, onClose, showFeedback }) => {
    const [testData, setTestData] = useState('');

    const handleTest = () => {
        try {
            const parsedData = JSON.parse(testData);
            onTest(integration.id, parsedData);
        } catch (error) {
            showFeedback('Invalid JSON data', 'error');
        }
    };

    return (
        <div
            style={{
                backgroundColor: "#fff",
                padding: "20px",
                borderRadius: "8px",
                width: "900px",
                maxHeight: "90vh",
                overflowY: "auto",
                position: "relative",
            }}
        >
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "20px",
                    paddingBottom: "10px",
                    borderBottom: "1px solid #e0e0e0",
                }}
            >
                <h2 style={{ margin: 0 }}>Test Integration - {integration.name}</h2>
                <button
                    onClick={onClose}
                    style={{
                        background: "none",
                        border: "none",
                        fontSize: "24px",
                        cursor: "pointer",
                        color: "#666",
                    }}
                >
                    ×
                </button>
            </div>
            
            <div className="modal-body">
                <div className="form-group">
                    <label>Test Data (JSON)</label>
                    <textarea
                        value={testData}
                        onChange={(e) => setTestData(e.target.value)}
                        placeholder='{"event_name": "Test Event", "event_date": "2024-01-15"}'
                        rows="6"
                    />
                </div>

                {testResults && (
                    <div className="test-results">
                        <h3>Test Results</h3>
                        <div className={`result-status ${testResults.success ? 'success' : 'error'}`}>
                            {testResults.success ? '✓ Success' : '✗ Failed'}
                        </div>
                        
                        {testResults.mapped_data && (
                            <div className="mapped-data">
                                <h4>Mapped Data:</h4>
                                <pre>{JSON.stringify(testResults.mapped_data, null, 2)}</pre>
                            </div>
                        )}

                        {testResults.errors.length > 0 && (
                            <div className="errors">
                                <h4>Errors:</h4>
                                <ul>
                                    {testResults.errors.map((error, index) => (
                                        <li key={index}>{error}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {testResults.warnings.length > 0 && (
                            <div className="warnings">
                                <h4>Warnings:</h4>
                                <ul>
                                    {testResults.warnings.map((warning, index) => (
                                        <li key={index}>{warning}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
            </div>
            
            <div
                style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "10px",
                    padding: "20px",
                    borderTop: "1px solid #e0e0e0",
                }}
            >
                <button className="btn btn-secondary" onClick={onClose}>
                    Close
                </button>
                <button className="btn btn-primary" onClick={handleTest}>
                    Run Test
                </button>
            </div>
        </div>
    );
};

// Field Mappings Modal Component
const FieldMappingsModal = ({ integration, fieldMappings, onClose }) => {
    return (
        <div
            style={{
                backgroundColor: "#fff",
                padding: "20px",
                borderRadius: "8px",
                width: "900px",
                maxHeight: "90vh",
                overflowY: "auto",
                position: "relative",
            }}
        >
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "20px",
                    paddingBottom: "10px",
                    borderBottom: "1px solid #e0e0e0",
                }}
            >
                <h2 style={{ margin: 0 }}>Field Mappings - {integration.name}</h2>
                <button
                    onClick={onClose}
                    style={{
                        background: "none",
                        border: "none",
                        fontSize: "24px",
                        cursor: "pointer",
                        color: "#666",
                    }}
                >
                    ×
                </button>
            </div>
            
            <div className="modal-body">
                {fieldMappings.length === 0 ? (
                    <p>No field mappings configured for this integration.</p>
                ) : (
                    <div className="field-mappings-table">
                        <table>
                            <thead>
                                <tr>
                                    <th>External Field</th>
                                    <th>Internal Field</th>
                                    <th>Entity Type</th>
                                    <th>Field Type</th>
                                </tr>
                            </thead>
                            <tbody>
                                {fieldMappings.map((mapping, index) => (
                                    <tr key={index}>
                                        <td>{mapping.external_field_name}</td>
                                        <td>{mapping.internal_field_name}</td>
                                        <td>{mapping.entity_type}</td>
                                        <td>{mapping.field_type}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            
            <div
                style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "10px",
                    padding: "20px",
                    borderTop: "1px solid #e0e0e0",
                }}
            >
                <button className="btn btn-primary" onClick={onClose}>
                    Close
                </button>
            </div>
        </div>
    );
};

// Credentials Modal Component
const CredentialsModal = ({ integration, onClose, showFeedback }) => {
    console.log('CredentialsModal rendering with integration:', integration);
    
    const apiEndpoint = `${process.env.REACT_APP_API_BASE_URL}/api/v1/crm-source/events`;
    
    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            showFeedback('Copied to clipboard!');
        }).catch(() => {
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = text;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            showFeedback('Copied to clipboard!');
        });
    };

    return (
        <div
            style={{
                backgroundColor: "#fff",
                padding: "20px",
                borderRadius: "8px",
                width: "900px",
                maxHeight: "90vh",
                overflowY: "auto",
                position: "relative",
            }}
        >
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "20px",
                    paddingBottom: "10px",
                    borderBottom: "1px solid #e0e0e0",
                }}
            >
                <h2 style={{ margin: 0 }}>API Credentials - {integration?.name || 'Unknown'}</h2>
                <button
                    onClick={onClose}
                    style={{
                        background: "none",
                        border: "none",
                        fontSize: "24px",
                        cursor: "pointer",
                        color: "#666",
                    }}
                >
                    ×
                </button>
            </div>
            
            <div className="modal-body">
                <div className="credentials-section">
                    <h3>API Endpoint</h3>
                    <p>Use this URL to send event data from your CRM:</p>
                    <div className="credential-field">
                        <code className="api-endpoint">{apiEndpoint}</code>
                        <button 
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => copyToClipboard(apiEndpoint)}
                        >
                            <i className="fas fa-copy"></i> Copy
                        </button>
                    </div>
                </div>

                <div className="credentials-section">
                    <h3>API Key</h3>
                    <p>Include this key in the <code>x-api-key</code> header with your requests:</p>
                    <div className="credential-field">
                        <code className="api-key">{integration?.api_key || 'No API key'}</code>
                        <button 
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => copyToClipboard(integration?.api_key || '')}
                        >
                            <i className="fas fa-copy"></i> Copy
                        </button>
                    </div>
                </div>

                <div className="credentials-section">
                    <h3>Example Request</h3>
                    <p>Here's how to use these credentials:</p>
                    <div className="example-request">
                        <pre>
{`curl -X POST "${apiEndpoint}" \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: ${integration?.api_key || 'YOUR_API_KEY'}" \\
  -d '{
    "events": [
      {
        "data": {
          "event_name": "Spring Open House",
          "event_date": "2024-03-15",
          "form_type": "Undergraduate"
        }
      }
    ]
  }'`}
                        </pre>
                        <button 
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => copyToClipboard(`curl -X POST "${apiEndpoint}" \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: ${integration?.api_key || 'YOUR_API_KEY'}" \\
  -d '{
    "events": [
      {
        "data": {
          "event_name": "Spring Open House",
          "event_date": "2024-03-15",
          "form_type": "Undergraduate"
        }
      }
    ]
  }'`)}
                        >
                            <i className="fas fa-copy"></i> Copy cURL
                        </button>
                    </div>
                </div>

                <div className="credentials-section">
                    <h3>Security Note</h3>
                    <div className="alert alert-warning">
                        <i className="fas fa-exclamation-triangle"></i>
                        <strong>Important:</strong> Keep your API key secure and never share it publicly. 
                        If you suspect your key has been compromised, use the "Regenerate API Key" button 
                        to create a new one.
                    </div>
                </div>
            </div>
            
            <div
                style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "10px",
                    padding: "20px",
                    borderTop: "1px solid #e0e0e0",
                }}
            >
                <button className="btn btn-primary" onClick={onClose}>
                    Close
                </button>
            </div>
        </div>
    );
};

// General Instructions Component
const GeneralInstructions = ({ setActiveTab }) => {
    const navigate = useNavigate();

    // Sample payload for general instructions
    const samplePayload = {
        "tour_name": "Spring 2024 Campus Tour",
        "tour_description": "Comprehensive campus tour for prospective students",
        "event_name": "Spring Open House",
        "event_date": "2024-03-15",
        "event_type": "Open House",
        "form_type": "Undergraduate",
        "city": "Boston",
        "country": "USA",
        "capacity": 100,
        "registration_deadline": "2024-03-10",
        "location": "Main Campus - Student Center",
        "start_time": "09:00",
        "end_time": "16:00",
        "contact_email": "admissions@university.edu",
        "contact_phone": "+1-555-123-4567"
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            alert('Copied to clipboard!');
        });
    };

    return (
        <div className="general-instructions">
            <div className="instructions-header">
                <h2>CRM Source Integration Setup Guide</h2>
                <p>Complete guide for setting up integrations to receive events from external CRMs</p>
            </div>

            <div className="instructions-sections">
                {/* Overview Section */}
                <div className="instruction-section">
                    <h3>
                        <i className="fas fa-info-circle"></i>
                        Overview
                    </h3>
                    <div className="overview-content">
                        <p>CRM Source Integrations allow external Customer Relationship Management (CRM) systems to push event data directly into your Kai Capture system. This enables seamless data flow from your existing CRM to create tours and events automatically.</p>
                        
                        <div className="benefits-list">
                            <h4>Benefits:</h4>
                            <ul>
                                <li><strong>Automated Data Sync:</strong> Events are created automatically when pushed from your CRM</li>
                                <li><strong>Flexible Field Mapping:</strong> Map your CRM fields to Kai system fields</li>
                                <li><strong>Real-time Updates:</strong> Changes in your CRM are reflected immediately</li>
                                <li><strong>Secure API Access:</strong> Each integration has its own API key for security</li>
                                <li><strong>Duplicate Prevention:</strong> System checks for existing tours/events before creating new ones</li>
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Prerequisites Section */}
                <div className="instruction-section">
                    <h3>
                        <i className="fas fa-clipboard-check"></i>
                        Prerequisites
                    </h3>
                    <div className="prerequisites-content">
                        <ul>
                            <li>Access to your external CRM system</li>
                            <li>Ability to configure webhooks or API calls from your CRM</li>
                            <li>Knowledge of your CRM's data structure and field names</li>
                            <li>Network access to make HTTP requests to the Kai Capture API</li>
                        </ul>
                    </div>
                </div>

                {/* Setup Process Section */}
                <div className="instruction-section">
                    <h3>
                        <i className="fas fa-cogs"></i>
                        Setup Process
                    </h3>
                    <div className="setup-process">
                        <div className="step-item">
                            <div className="step-number">1</div>
                            <div className="step-content">
                                <h4>Create Integration</h4>
                                <p>Go to the <strong>Integrations</strong> tab and click "Create Integration". Provide a name and description for your integration.</p>
                            </div>
                        </div>
                        
                        <div className="step-item">
                            <div className="step-number">2</div>
                            <div className="step-content">
                                <h4>Configure Field Mappings</h4>
                                <p>Map your CRM's field names to Kai system fields. Start with tour fields (parent entities), then map event fields (child entities). For example, if your CRM uses "program_name", map it to "Tour Name".</p>
                            </div>
                        </div>
                        
                        <div className="step-item">
                            <div className="step-number">3</div>
                            <div className="step-content">
                                <h4>Get API Credentials</h4>
                                <p>After creating the integration, you'll receive an API endpoint URL and API key. These are used to authenticate requests from your CRM.</p>
                            </div>
                        </div>
                        
                        <div className="step-item">
                            <div className="step-number">4</div>
                            <div className="step-content">
                                <h4>Configure Your CRM</h4>
                                <p>In your CRM system, set up webhooks or scheduled API calls to send event data to the provided endpoint using the API key for authentication.</p>
                            </div>
                        </div>
                        
                        <div className="step-item">
                            <div className="step-number">5</div>
                            <div className="step-content">
                                <h4>Test the Integration</h4>
                                <p>Use the test feature to verify your integration is working correctly before going live.</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Field Mapping Guide Section */}
                <div className="instruction-section">
                    <h3>
                        <i className="fas fa-map"></i>
                        Field Mapping Guide
                    </h3>
                    <div className="field-mapping-guide-general">
                        <p>When creating your integration, you'll need to map your CRM's field names to Kai system fields. Here are the most common mappings:</p>
                        
                        <div className="mapping-categories">
                            <div className="mapping-category">
                                <h4>Tour Fields</h4>
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Kai System Field</th>
                                            <th>Description</th>
                                            <th>Example CRM Field</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td><strong>Tour ID</strong></td>
                                            <td>Unique identifier for the tour</td>
                                            <td><code>tour_id</code>, <code>id</code>, <code>program_id</code></td>
                                        </tr>
                                        <tr>
                                            <td><strong>Tour Name</strong></td>
                                            <td>Name of the tour</td>
                                            <td><code>tour_name</code>, <code>program_name</code>, <code>campaign_name</code></td>
                                        </tr>
                                        <tr>
                                            <td><strong>Country</strong></td>
                                            <td>Country where the tour takes place</td>
                                            <td><code>country</code>, <code>location_country</code></td>
                                        </tr>
                                        <tr>
                                            <td><strong>Tour Start Date</strong></td>
                                            <td>Tour start date (YYYY-MM-DD)</td>
                                            <td><code>start_date</code>, <code>date_from</code>, <code>begin_date</code></td>
                                        </tr>
                                        <tr>
                                            <td><strong>Tour End Date</strong></td>
                                            <td>Tour end date (YYYY-MM-DD)</td>
                                            <td><code>end_date</code>, <code>date_to</code>, <code>finish_date</code></td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            
                            <div className="mapping-category">
                                <h4>Event Fields</h4>
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Kai System Field</th>
                                            <th>Description</th>
                                            <th>Example CRM Field</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td><strong>Event Name</strong></td>
                                            <td>Name of the event</td>
                                            <td><code>title</code>, <code>event_title</code>, <code>name</code></td>
                                        </tr>
                                        <tr>
                                            <td><strong>Event Date</strong></td>
                                            <td>Event date (YYYY-MM-DD)</td>
                                            <td><code>date</code>, <code>event_date</code>, <code>start_date</code></td>
                                        </tr>
                                        <tr>
                                            <td><strong>Event Type</strong></td>
                                            <td>Type of event</td>
                                            <td><code>type</code>, <code>category</code>, <code>event_type</code></td>
                                        </tr>
                                        <tr>
                                            <td><strong>Form Type</strong></td>
                                            <td>Type of form to use for this event (e.g., Undergraduate, Graduate, Hybrid)</td>
                                            <td><code>form_type</code>, <code>program_type</code>, <code>student_type</code></td>
                                        </tr>
                                        <tr>
                                            <td><strong>City</strong></td>
                                            <td>Event city</td>
                                            <td><code>city</code>, <code>location_city</code>, <code>venue_city</code></td>
                                        </tr>
                                        <tr>
                                            <td><strong>Country</strong></td>
                                            <td>Event country</td>
                                            <td><code>country</code>, <code>location_country</code></td>
                                        </tr>
                                        <tr>
                                            <td><strong>External ID</strong></td>
                                            <td>Unique identifier for the event from your CRM system</td>
                                            <td><code>event_id</code>, <code>id</code>, <code>record_id</code>, <code>crm_id</code></td>
                                        </tr>
                                        <tr>
                                            <td><strong>Assigned User</strong></td>
                                            <td>ID of the user assigned to the event</td>
                                            <td><code>assigned_user_id</code>, <code>owner_id</code>, <code>contact_id</code></td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Sample Payload Section */}
                <div className="instruction-section">
                    <h3>
                        <i className="fas fa-code"></i>
                        Sample Payload Format
                    </h3>
                    <div className="sample-payload-container">
                        <div className="payload-header">
                            <span>Example JSON payload structure</span>
                            <button 
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => copyToClipboard(JSON.stringify(samplePayload, null, 2))}
                            >
                                <i className="fas fa-copy"></i> Copy JSON
                            </button>
                        </div>
                        <pre className="sample-payload">
                            {JSON.stringify(samplePayload, null, 2)}
                        </pre>
                    </div>
                </div>

                {/* Next Steps Section */}
                <div className="instruction-section">
                    <h3>
                        <i className="fas fa-arrow-right"></i>
                        Next Steps
                    </h3>
                    <div className="next-steps">
                        <p>Ready to create your first integration?</p>
                        <button 
                            className="btn btn-primary" 
                            onClick={() => setActiveTab('integrations')}
                        >
                            <i className="fas fa-plus"></i> Create Your First Integration
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CRMSourceIntegrationManagement; 